/**
 * The two screens the auth gate shows before it knows who is signed in.
 *
 * ============================================================================
 * WHY THESE EXIST. `isAuthenticated: false` used to mean two different things — "we
 * asked and nobody is signed in" and "we have not asked yet" — and the gate could only
 * act on the second as though it were the first. stores/authStore.ts documents that
 * three-state problem at `sessionChecked`; these are the two screens the third state
 * needs, so the gate can say "checking" and "the check failed" instead of showing a
 * login form in both cases.
 *
 * The distinction is not cosmetic. Telling someone with a valid session that they are
 * logged out invites them to re-type a password at a server that cannot answer, and the
 * password will appear to be wrong. That is the defect this file's two states prevent.
 * ============================================================================
 *
 * PRESENTATIONAL ONLY — no store, no router, no fetch. The gate owns the decision and
 * passes a retry in; these render it. That keeps them mountable in a test without a
 * router, which is how the gate's states are asserted.
 */
import { Btn } from '../ui';
import Logo from '../ui/Logo';
import { c } from '../../styles/theme';

/**
 * Full-height centred frame, shared by both states so they cannot drift apart.
 *
 * `100dvh` rather than `100vh`: on mobile Safari `vh` is the viewport WITHOUT the
 * dynamic toolbar, so a `100vh` splash is taller than the visible area and the content
 * a centred box holds sits below the fold on the one screen where this is all there is.
 * The rest of the app's full-height surfaces use the same unit.
 */
function GateFrame({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        // Padding rather than a fixed width, so the failure state's sentence has room
        // to wrap on a 380px phone instead of being clipped.
        padding: '24px 20px',
        textAlign: 'center',
        background: c.bg,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Shown while `restoreSession()` is in flight.
 *
 * `role="status"` with a polite live region, and a visible sentence rather than a bare
 * spinner: a screen reader on a spinner-only splash announces nothing at all, so the
 * app appears to have stopped. `aria-busy` marks the region as still resolving.
 *
 * NO SPINNER ANIMATION BY DEFAULT — the brand mark and the sentence carry it. An
 * indefinite animated spinner is the thing `prefers-reduced-motion` exists to suppress,
 * and there is nothing here worth building a motion exception for.
 */
export function SessionCheckingSplash() {
  return (
    <GateFrame>
      <Logo size={30} />
      <p role="status" aria-busy="true" style={{ fontSize: 13, color: c.text2, margin: 0 }}>
        Checking your session…
      </p>
    </GateFrame>
  );
}

export interface SessionErrorScreenProps {
  /** The message from `authStore.sessionError` — already a human sentence. */
  message: string;
  /** Runs `restoreSession()` again. */
  onRetry: () => void;
  /** Abandons the check and goes to the sign-in screen. */
  onSignIn: () => void;
}

/**
 * Shown when the session check itself failed — an outage, not a signed-out user.
 *
 * TWO ACTIONS, AND BOTH ARE NECESSARY. Retry is the honest primary: the session may be
 * perfectly valid and the request simply did not arrive. Sign in is the escape hatch,
 * because a backend that stays down would otherwise trap someone on this screen with
 * nothing to do — and someone who genuinely has no account needs a way through.
 *
 * The message comes from the API error contract (`ApiErrorShape.message`), not from a
 * sentence written here: api/errors.ts already guarantees a user-appropriate string for
 * every failure kind, including the network case, and inventing a second one here would
 * be the per-consumer error text that contract exists to stop.
 */
export function SessionErrorScreen({ message, onRetry, onSignIn }: SessionErrorScreenProps) {
  return (
    <GateFrame>
      <Logo size={30} />
      <div style={{ maxWidth: 340 }}>
        <h1
          style={{
            fontFamily: 'Space Grotesk',
            fontWeight: 600,
            fontSize: 16,
            margin: '0 0 6px',
            color: c.text1,
          }}
        >
          We could not check your session
        </h1>
        {/*
          `role="alert"` so it is announced when it appears — this replaces the whole
          app, which is not a change a sighted user can miss and a screen-reader user
          otherwise would.
        */}
        <p role="alert" style={{ fontSize: 12.5, color: c.text2, margin: 0, lineHeight: 1.6 }}>
          {message}
        </p>
      </div>
      {/* Wraps, because two buttons plus their labels do not fit on one line at 380px. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Btn onClick={onRetry}>Try again</Btn>
        <Btn variant="secondary" onClick={onSignIn}>
          Sign in
        </Btn>
      </div>
    </GateFrame>
  );
}
